import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import { buildAideContact } from "@/lib/aide-contact";
import type { FormDefinition } from "@/lib/api";
import { initiateVisitCall } from "@/lib/api";
import { useNotification } from "@/lib/notifications";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

interface AideCardProps {
    visitId: string;
    visit: FormDefinition["visit"];
}

// Who the nurse is here to evaluate. Rendered only on aide-evaluation visits
// (see isAideEvaluationVisit). Shows the aide's name and phone number in the
// clear so the nurse can check them against the person in front of her; the
// call button still goes through the server bridge like every other contact.
// Call outcome toasts (answered / no answer) come from PatientContacts'
// call:updated listener, which is mounted on the same screen.
export function AideCard({ visitId, visit }: AideCardProps) {
    const aide = buildAideContact(visit);
    const { showNotification } = useNotification();
    const [calling, setCalling] = useState(false);

    async function handleCall() {
        if (!aide?.contactRef || calling) {
            return;
        }
        setCalling(true);
        showNotification(
            `You'll get a call shortly — answer to be connected to ${aide.name}.`,
            "info"
        );
        try {
            await initiateVisitCall(visitId, aide.contactRef);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Couldn't start the call";
            showNotification(message, "failed");
        } finally {
            setCalling(false);
        }
    }

    return (
        <View style={styles.card}>
            <Text style={styles.sectionLabel}>AIDE TO EVALUATE</Text>

            {aide ? (
                <View style={styles.row}>
                    <View style={styles.avatar}>
                        <Text style={styles.avatarText}>{aide.initials}</Text>
                    </View>
                    <View style={styles.info}>
                        <Text
                            style={[styles.name, !aide.hasName && styles.namePlaceholder]}
                            numberOfLines={2}
                        >
                            {aide.name}
                        </Text>
                        {aide.phoneDisplay ? (
                            <Text style={styles.phone} selectable>
                                {aide.phoneDisplay}
                            </Text>
                        ) : (
                            <Text style={styles.phoneMissing}>No phone number on file</Text>
                        )}
                    </View>
                    {aide.contactRef ? (
                        <Pressable
                            onPress={handleCall}
                            disabled={calling}
                            accessibilityLabel={`Call ${aide.name}`}
                            style={({ pressed }) => [
                                styles.callButton,
                                pressed && styles.callPressed
                            ]}
                        >
                            {calling ? (
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                                <MaterialCommunityIcons name="phone" size={18} color="#FFFFFF" />
                            )}
                        </Pressable>
                    ) : null}
                </View>
            ) : (
                <Text style={styles.emptyText}>
                    No aide on file for this visit. Confirm the aide&apos;s name with the agency and
                    enter it in the evaluation.
                </Text>
            )}

            <View style={styles.hintRow}>
                <MaterialCommunityIcons
                    name="shield-check-outline"
                    size={16}
                    color={colors.warning600}
                />
                <Text style={styles.hintText}>
                    Check that the name and phone number match the aide you&apos;re working with
                    before you start.
                </Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.md
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md
    },
    avatar: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    avatarText: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.teal600
    },
    info: {
        flex: 1,
        minWidth: 0
    },
    name: {
        fontFamily: fonts.bold,
        fontSize: 17,
        color: colors.textPrimary,
        lineHeight: 22
    },
    namePlaceholder: {
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        fontStyle: "italic"
    },
    phone: {
        fontFamily: fonts.semiBold,
        fontSize: 15,
        color: colors.textPrimary,
        marginTop: 2,
        letterSpacing: 0.3
    },
    phoneMissing: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textTertiary,
        fontStyle: "italic",
        marginTop: 2
    },
    callButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.teal400,
        alignItems: "center",
        justifyContent: "center"
    },
    callPressed: {
        backgroundColor: colors.teal500
    },
    emptyText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 18
    },
    hintRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        marginTop: spacing.md,
        paddingTop: spacing.md,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    hintText: {
        flex: 1,
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary,
        lineHeight: 17
    }
});
